/* eslint-disable max-lines */
import { track } from "jiffy-analytics";
import { api } from "jiffy-api";
import { getCountryId } from "common/utils/user-context";
import { showTwoFactorModal } from "components/jiffy/masthead-redesign2025/local-delivery/utils/two-factor-modal-helper";
import { AddressPayloadBuilder } from "components/jiffy/masthead-redesign2025/local-delivery/utils/address-payload-builder";
import AddressApiService from "components/jiffy/masthead-redesign2025/local-delivery/address-saver/address-api-service";
import AddressErrorHandler from "components/jiffy/masthead-redesign2025/local-delivery/address-saver/address-error-handler";
import UserContextHelper from "components/jiffy/masthead-redesign2025/local-delivery/utils/user-context-helper";

const MIXPANEL_EVENTS = {
  VALIDATION_FAILED: "address_validation_failed",
  SAVE_COMPLETED: "address_save_completed"
};

/**
 * AddressSaver
 *
 * Main orchestrator for saving addresses from SmartyStreets autocomplete.
 * Coordinates validation, transformation, API calls, and error handling.
 * Supports both guest and logged-in users with proper context handling.
 *
 * @example
 * const addressSaver = new AddressSaver({
 *   errorContainerSelector: ".js-errors",
 *   onSuccess: (data) => console.log("Saved:", data),
 *   onError: (error) => console.error("Error:", error),
 *   onLoadingChange: (isLoading) => console.log("Loading:", isLoading)
 * });
 *
 * await addressSaver.saveAddress(smartyStreetsAddress, { phone: "555-1234" });
 * console.log(addressSaver.isLoading); // Check loading state
 */

export default class AddressSaver {
  /** @type {AddressErrorHandler} */
  #errorHandler = null;

  #options;

  #isLoading = false;

  #currentValidatingAddress = null;

  #DEFAULT_ERROR_CONTAINER_SELECTOR = ".js-smarty-streets-autocomplete";

  static #MESSAGES = {
    ERROR_HANDLER_INIT_FAILED: "Could not initialize error handler",
    SAVE_IN_PROGRESS: "An address save is already in progress",
    UNEXPECTED_ERROR: "An unexpected error occurred",
    UNEXPECTED_ERROR_DETAILED:
      "An unexpected error occurred while saving the address. Please try again.",
    TRACKING_FAILED: "AddressSaver: Tracking failed",
    HONEYBADGER_CONTEXT: "AddressSaver: Unexpected error"
  };

  /**
   * Creates a new AddressSaver instance
   *
   * @param {Object} [options={}] - Configuration options
   * @param {string} [options.errorContainerSelector] - CSS selector for error container
   * @param {Function} [options.onSuccess] - Success callback (data) => void
   * @param {Function} [options.onError] - Error callback (error) => void
   * @param {Function} [options.onLoadingChange] - Loading state change callback (isLoading) => void
   */
  constructor(options = {}) {
    this.#options = {
      errorContainerSelector:
        options.errorContainerSelector || this.#DEFAULT_ERROR_CONTAINER_SELECTOR,
      onSuccess: options.onSuccess || null,
      onError: options.onError || null,
      onLoadingChange: options.onLoadingChange || null
    };

    // Initialize error handler if container selector is provided
    try {
      if (this.#options.errorContainerSelector) {
        this.#errorHandler = new AddressErrorHandler(this.#options.errorContainerSelector);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(AddressSaver.#MESSAGES.ERROR_HANDLER_INIT_FAILED, error);
      this.#errorHandler = null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  get #$panel() {
    return document.querySelector('.js-slide-panel[data-panel-id="local-delivery-slide-panel"]');
  }

  /**
   * Gets the current loading state
   *
   * @returns {boolean} True if currently saving an address
   */
  get isLoading() {
    return this.#isLoading;
  }

  /**
   * Sets the loading state and triggers callback
   *
   * @param {boolean} value - New loading state
   * @private
   */
  #setLoading(value) {
    this.#isLoading = value;

    this.#options.onLoadingChange?.(value);
  }

  /**
   * Saves an address (creates new or updates existing)
   * Main entry point for address saving operations
   *
   * @param {Object} smartyStreetsAddress - Address from SmartyStreets autocomplete
   * @param {string} smartyStreetsAddress.street_line - Street address
   * @param {string} [smartyStreetsAddress.secondary] - Apt/suite/unit
   * @param {string} smartyStreetsAddress.city - City name
   * @param {string} smartyStreetsAddress.state - State abbreviation
   * @param {string} smartyStreetsAddress.zipcode - ZIP code
   * @param {Object} [options={}] - Additional options
   * @param {number} [options.addressId] - If provided, updates existing address
   * @param {string} [options.phone] - Phone number
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async saveAddress(smartyStreetsAddress, options = {}) {
    // Prevent concurrent saves
    if (this.#isLoading) {
      // eslint-disable-next-line no-console
      console.error(AddressSaver.#MESSAGES.SAVE_IN_PROGRESS);
      return;
    }

    // Store address for validation tracking
    const isSaved = smartyStreetsAddress.isSaved === true;
    this.#currentValidatingAddress = {
      ...smartyStreetsAddress,
      entryMethod: isSaved ? "saved" : "autosuggest"
    };

    // Set loading state
    this.#setLoading(true);

    // Clear any previous errors
    this.clearErrors();

    try {
      await this.#validateAddress(smartyStreetsAddress, options);
    } catch (error) {
      this.#setLoading(false);
      this.#handleUnexpectedError(error, options);
    }
  }

  async #validateAddress(smartyStreetsAddress, options) {
    const validation = await api.jiffyshirts.validateAddress({
      address: {
        ...smartyStreetsAddress,
        address1: smartyStreetsAddress.street_line,
        country_id: getCountryId()
      },
      local_delivery_address: true,
      business_line_path: "print"
    });

    if (validation.errors) {
      this.#setLoading(false);
      this.#handleError(validation.errors);
      return;
    }

    if (validation.two_factor_needed) {
      showTwoFactorModal({
        saveAddress: authToken =>
          this.#executeSaveAddress(smartyStreetsAddress, { ...options, authToken }),
        setLoading: isLoading => this.#setLoading(isLoading),
        errorMessageElement: null,
        addressId: null,
        targetContainer: this.#$panel
      });
    } else {
      this.#executeSaveAddress(smartyStreetsAddress, options);
    }
  }

  async #executeSaveAddress(smartyStreetsAddress, options) {
    const userContext = UserContextHelper.getUserContext();
    const payloadOptions = { ...options };

    if (!userContext.isLoggedIn) payloadOptions.orderNumber = userContext.orderNumber;

    const apiPayload = AddressSaver.#toApiFormat(smartyStreetsAddress, payloadOptions);

    this.#setLoading(true);
    const result = await AddressApiService.saveAddress(apiPayload, options);

    if (result.success) {
      this.#handleSuccess(result.data, smartyStreetsAddress, options);
      return;
    }

    this.#handleError(result.error);
    this.#setLoading(false);
  }

  /**
   * Transforms SmartyStreets address format to API format
   *
   * SmartyStreets format:
   * {
   *   street_line: "123 Main St",
   *   secondary: "Apt 4B",
   *   city: "Los Angeles",
   *   state: "CA",
   *   zipcode: "90001"
   * }
   *
   * API format:
   * {
   *   address1: "123 Main St",
   *   address2: "Apt 4B",
   *   city: "Los Angeles",
   *   state_name: "CA",
   *   zipcode: "90001",
   *   local_delivery_address: true,
   *   order_number: "R123456",
   *   phone: "555-1234"
   * }
   *
   * @param {Object} smartyStreetsAddress - Address from SmartyStreets autocomplete
   * @param {string} smartyStreetsAddress.street_line - Street address
   * @param {string} [smartyStreetsAddress.secondary] - Apt/suite/unit
   * @param {string} smartyStreetsAddress.city - City name
   * @param {string} smartyStreetsAddress.state - State abbreviation
   * @param {string} smartyStreetsAddress.zipcode - ZIP code
   * @param {Object} [options={}] - Additional options
   * @param {string} [options.phone] - Phone number
   * @param {string} [options.fullName] - Full name
   * @param {string} [options.orderNumber] - Order number (for all users)
   * @param {number} [options.addressId] - Address ID (for updates)
   * @param {string} [options.authToken] - Authentication token (for 2FA)
   * @returns {Object} Address in API format
   */
  static #toApiFormat(smartyStreetsAddress, options = {}) {
    if (!smartyStreetsAddress || typeof smartyStreetsAddress !== "object") {
      throw new Error("Smarty streets address is required");
    }

    return AddressPayloadBuilder.buildPayload(
      {
        address1: smartyStreetsAddress.street_line,
        address2: smartyStreetsAddress.secondary,
        city: smartyStreetsAddress.city,
        stateName: smartyStreetsAddress.state,
        zipcode: smartyStreetsAddress.zipcode
      },
      {
        phone: options.phone,
        fullName: options.fullName,
        authToken: options.authToken,
        userId: options.userId,
        orderNumber: options.orderNumber
      }
    );
  }

  /**
   * Handles successful address save
   *
   * @param {Object} data - Saved address data
   * @param {Object} originalAddress - Original SmartyStreets address
   * @param {Object} options - Save options
   * @private
   */
  #handleSuccess(data, originalAddress, options) {
    // Clear errors
    this.clearErrors();

    // Track success
    this.#trackEvent("address_save_success", {
      addressId: data.id,
      isUpdate: Boolean(options.addressId),
      city: originalAddress.city,
      state: originalAddress.state
    });

    // Track new address save completion (not updates)
    if (!options.addressId) {
      this.#trackAddressSaveCompleted(data);
    }

    // Call success callback
    if (this.#options.onSuccess) {
      this.#options.onSuccess(data);
    }
  }

  /**
   * Tracks when new address is successfully saved
   * @param {Object} savedAddress - The saved address data
   * @private
   */
  #trackAddressSaveCompleted(savedAddress) {
    if (!this.#currentValidatingAddress) {
      return;
    }

    track(MIXPANEL_EVENTS.SAVE_COMPLETED, {
      address_id: String(savedAddress.id),
      entry_method: this.#currentValidatingAddress.entryMethod || "autosuggest",
      zipcode: savedAddress.zipcode || this.#currentValidatingAddress.zipcode || "",
      verification_status: savedAddress.verifiedAt ? "verified" : "unverified",
      is_local_delivery_eligible: true,
      component_type: "modal"
    });

    // Reset validation tracking
    this.#currentValidatingAddress = null;
  }

  /**
   * Determines failure reason from error message
   * @param {string|Object} error - Error message or error object
   * @returns {string} Failure reason
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #getFailureReason(error) {
    if (!error) return "api_error";

    // Convert error to string if it's an object
    const errorString = typeof error === "string" ? error : JSON.stringify(error);
    const errorLower = errorString.toLowerCase();

    if (errorLower.includes("po box") || errorLower.includes("p.o. box")) {
      return "po_box";
    }

    if (errorLower.includes("invalid") || errorLower.includes("format")) {
      return "invalid_format";
    }

    return "api_error";
  }

  /**
   * Tracks validation failure event
   * @param {string|Object} error - Error message or error object
   * @private
   */
  #trackValidationFailed(error) {
    if (!this.#currentValidatingAddress) {
      return;
    }

    const failureReason = this.#getFailureReason(error);
    // Convert error to string for tracking
    const errorMessage = typeof error === "string" ? error : JSON.stringify(error);

    track(MIXPANEL_EVENTS.VALIDATION_FAILED, {
      entry_method: this.#currentValidatingAddress.entryMethod || "autosuggest",
      zipcode: this.#currentValidatingAddress.zipcode || "",
      failure_reason: failureReason,
      error_message: errorMessage || "Unknown error",
      component_type: "modal"
    });

    // Reset validation tracking
    this.#currentValidatingAddress = null;
  }

  /**
   * Handles API or processing errors
   *
   * @param {string} error - Error message
   * @private
   */
  #handleError(error) {
    this.#trackValidationFailed(error);
    this.#errorHandler?.show(error);

    this.#options?.onError?.({ type: "api", error });

    // eslint-disable-next-line no-console
    console.error("Address save failed", error);
  }

  /**
   * Handles unexpected errors
   *
   * @param {Error} error - Error object
   * @param {Object} options - Context options
   * @private
   */
  #handleUnexpectedError(error, options) {
    // Track validation failure
    this.#trackValidationFailed(error?.message || AddressSaver.#MESSAGES.UNEXPECTED_ERROR);

    // eslint-disable-next-line no-console
    console.error(AddressSaver.#MESSAGES.HONEYBADGER_CONTEXT, error);

    // Show generic error to user
    this.#errorHandler?.show(AddressSaver.#MESSAGES.UNEXPECTED_ERROR_DETAILED);

    this.#options?.onError?.({ type: "unexpected", error: error?.message });

    // Log to Honeybadger if available
    if (typeof window !== "undefined" && window.Honeybadger) {
      window.Honeybadger.notify(AddressSaver.#MESSAGES.HONEYBADGER_CONTEXT, {
        params: {
          error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          addressId: options.addressId,
          hasPhone: Boolean(options.phone)
        }
      });
    }
  }

  /**
   * Clears error messages
   *
   * @public
   */
  clearErrors() {
    this.#errorHandler?.clear();
  }

  /**
   * Tracks analytics events
   *
   * @param {string} eventName - Event name
   * @param {Object} data - Event data
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackEvent(eventName, data = {}) {
    try {
      track(eventName, {
        ...data,
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(AddressSaver.#MESSAGES.TRACKING_FAILED, error);
    }
  }
}
